import { SxProps, Theme } from '@mui/material/styles'

export const gridContainer: SxProps<Theme> = {
  overflow: 'auto',
  mt: 2,
  position: 'relative',
}

export const timeHeaderCell: SxProps<Theme> = {
  fontWeight: 'bold',
  fontSize: '0.75rem',
  textAlign: 'center',
  borderBottom: '2px solid',
  borderColor: 'divider',
  px: 0.5,
  py: 1,
  height: 55,
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backgroundColor: 'background.paper',
  width: 90,
  minWidth: 90,
}

export const instructorHeaderCell: SxProps<Theme> = {
  position: 'sticky',
  left: 0,
  top: 0,
  zIndex: 4,
  backgroundColor: 'background.paper',
  borderBottom: '2px solid',
  borderColor: 'divider',
  height: 55,
  minWidth: 140,
}

export const instructorCell: SxProps<Theme> = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  backgroundColor: 'background.paper',
  borderRight: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'divider',
  py: 0.5,
  px: 1,
  minWidth: 140,
  whiteSpace: 'nowrap',
}

export const emptyCell: SxProps<Theme> = {
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  borderBottom: '1px solid',
  borderRight: '1px solid',
  borderColor: 'divider',
  p: 0,
  width: 90,
  minWidth: 90,
}

const NEUTRAL_COLOR = '#E0E0E0'

export const unavailableCell: SxProps<Theme> = {
  borderBottom: '1px solid',
  borderRight: '1px solid',
  borderColor: 'divider',
  p: 0,
  width: 90,
  minWidth: 90,
  background: `repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 4px,
    rgba(0, 0, 0, 0.06) 4px,
    rgba(0, 0, 0, 0.06) 8px
  )`,
  cursor: 'default',
}

export const getStatusBackgroundColor = (theme: Theme, status: string, paymentStatus: string): string => {
  if (status === 'cancelled') return theme.palette.error.main
  if (status === 'completed') return theme.palette.primary.main
  if (paymentStatus === 'paid') return theme.palette.success.main
  return theme.palette.info.main
}

export const getStatusBackgroundStyle = (
  theme: Theme,
  status: string,
  paymentStatus: string,
  isSelected: boolean,
  paidRatio?: number,
): Record<string, unknown> => {
  if (paymentStatus === 'partially_paid' && paidRatio !== undefined && paidRatio > 0 && paidRatio < 1) {
    const green = theme.palette.success.main + (isSelected ? '60' : '30')
    const blue = theme.palette.info.main + (isSelected ? '60' : '30')
    const pct = Math.round(paidRatio * 100)
    return {
      background: `linear-gradient(to right, ${green} ${pct}%, ${blue} ${pct}%)`,
    }
  }
  const c = getStatusBackgroundColor(theme, status, paymentStatus)
  return {
    backgroundColor: isSelected ? c + '60' : c + '30',
  }
}

export const lessonBlock = (
  theme: Theme,
  color: string | undefined,
  isCancelled: boolean,
  isCompleted: boolean,
  status: string,
  paymentStatus: string,
  isSelected: boolean,
  paidRatio?: number,
): SxProps<Theme> => {
  const c = color ?? NEUTRAL_COLOR
  const statusBgColor = getStatusBackgroundColor(theme, status, paymentStatus)
  return {
    ...getStatusBackgroundStyle(theme, status, paymentStatus, isSelected, paidRatio),
    borderTop: `4px solid ${c}`,
    borderRadius: 1,
    py: 0.5,
    px: 1,
    cursor: 'pointer',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    opacity: isCancelled ? 0.5 : 1,
    textDecoration: isCancelled ? 'line-through' : 'none',
    '&:hover': {
      backgroundColor: statusBgColor + '50',
    },
    overflow: 'hidden',
    ...(isSelected && {
      boxShadow: '0 0 0 2px rgba(211, 47, 47, 0.5)',
    }),
  }
}

export const highlightedCell: SxProps<Theme> = {
  backgroundColor: 'rgba(25, 118, 210, 0.08)',
}

export const highlightedTimeHeader: SxProps<Theme> = {
  backgroundColor: 'rgba(25, 118, 210, 0.12)',
}

export const previewBlock: SxProps<Theme> = {
  backgroundColor: 'rgba(25, 118, 210, 0.15)',
  border: '2px dashed rgba(25, 118, 210, 0.5)',
  borderRadius: 1,
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  pointerEvents: 'none',
  zIndex: 3,
}
